import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { query, timeGuard } from '@confluence/query-preloader-tools';
import { getSSRRenderInputs, ssrPrepareErrorsClient } from '@confluence/ssr-utilities';
import { fg } from '@confluence/feature-gating';

import { fetchORSForSSR, getInitialState, getSmartLinks, type SmartLinkType } from './index';

import { ContentSmartLinksQuery } from './ContentSmartLinksQuery.graphql';
import { ContentSmartLinksQueryForDraft } from './ContentSmartLinksQueryForDraft.graphql';
import type {
	ContentSmartLinksQuery as ContentSmartLinksQueryType,
	ContentSmartLinksQueryVariables as ContentSmartLinksQueryVariablesType,
} from './__types__/ContentSmartLinksQuery';
import type {
	ContentSmartLinksQueryForDraft as ContentSmartLinksQueryForDraftType,
	ContentSmartLinksQueryVariablesForDraft as ContentSmartLinksQueryVariablesForDraftType,
} from './__types__/ContentSmartLinksQueryForDraft';
import {
	MAX_BATCH_ORS_SMARTLINKS,
	MAX_CONCURRENT_ORS_CALLS,
	SMART_LINK_MAX_TIME,
} from './preloadContentSmartLinksConstants';
import {
	groupSmartLinksByHostname,
	isFirstParty,
	mergeSmallThirdPartyBatches,
	splitLinksIntoBatches,
} from './smartLinkBatchHelpers';

declare global {
	interface Window {
		__SSR_SMARTLINKS__?: { [key: string]: any } | {};
	}
}

const preloadContentSmartlinksQuery = async (
	contentId: string,
	isDraft: boolean = false,
): Promise<SmartLinkType[]> => {
	if (isDraft) {
		const smartLinkDraftRes = await query<
			ContentSmartLinksQueryForDraftType,
			ContentSmartLinksQueryVariablesForDraftType
		>({
			query: ContentSmartLinksQueryForDraft,
			variables: {
				contentId,
			},
		});
		return getSmartLinks(smartLinkDraftRes, isDraft);
	}

	const smartLinksRes = await query<
		ContentSmartLinksQueryType,
		ContentSmartLinksQueryVariablesType
	>({
		query: ContentSmartLinksQuery,
		variables: {
			contentId,
		},
	});
	return getSmartLinks(smartLinksRes, isDraft);
};

export const getSmartLinksStats = (
	orsCalls: any[],
	smartLinks: any[],
	resolvedSmartLinks: any[],
) => {
	const totalLinks = Array.from(new Set<string>(smartLinks.map(({ resourceUrl }) => resourceUrl)));
	const resolvedLinks = new Set(
		resolvedSmartLinks
			.filter(({ status }) => status === 200)
			.map(({ body }) => body?.data?.url)
			.filter(Boolean),
	);

	return {
		fetch: orsCalls.length,
		total: totalLinks.length,
		resolved: resolvedLinks.size,
		...totalLinks.reduce(
			(acc, link: string) => {
				// It's just a heuristic we can't really know if the URL is first party without actually resolving it.
				if (isFirstParty(link)) {
					acc.firstParty.total++;
					if (resolvedLinks.has(link)) {
						acc.firstParty.resolved++;
					}
				} else {
					acc.thirdParty.total++;
					if (resolvedLinks.has(link)) {
						acc.thirdParty.resolved++;
					}
				}
				return acc;
			},
			{
				firstParty: { total: 0, resolved: 0 },
				thirdParty: { total: 0, resolved: 0 },
			},
		),
	};
};

async function fetchORSForSSRWithTimeout(links: SmartLinkType[], timeout: number) {
	const { customURLs, userContext } = getSSRRenderInputs();
	const objectResolverURL = customURLs?.['object-resolver-service']!;

	return timeGuard(
		async () => {
			try {
				const res = await fetchORSForSSR({
					// Despite the API has a timeout it does not respect it. It can easily go over time.
					url: `${objectResolverURL}?timeout=${timeout}`,
					body: JSON.stringify(links),
					userContext,
				});
				if (!res.ok) {
					throw new Error(`${res.status} ${res.statusText}`);
				}
				return res.json();
			} catch (e) {
				ssrPrepareErrorsClient.submitError(
					new Error(`Smart link content ORS call failed: ${e.message}`),
				);
			}
			return [];
		},
		[],
		timeout,
	);
}

/*
 * preloadContentSmartLinks tracks the entire preloaded chain of queries needed to SSR Content Smart Links. This consists of two calls:
 * 1. ContentSmartLinksQuery, a GQL call to fetch SmartLinks URLs and
 * 2. A call to ORS to resolve the metadata, which uses the links returned from the previous call
 * The entirety of this chain is currently best effort, which means that SmartLinks will either resolve ALL
 * links by the time the longest preloaded query resolves or NONE.
 *
 * If a timeout is provided, Smartlinks will try and resolve as many links as possible
 * within the provided timeout.
 */

export const preloadContentSmartLinks = async (contentId: string, isDraft: boolean = false) => {
	const smartLinks = await preloadContentSmartlinksQuery(contentId, isDraft);
	if (!smartLinks?.length) {
		return;
	}

	const timeout = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_max_time',
		SMART_LINK_MAX_TIME,
	);

	await SSRMeasures.run('ssr-app/prepare/preloadQuery/fetch:contentSmartLinksORS', async () => {
		const orsCalls: Promise<any>[] = [];

		if (fg('group_smart_link_resolution_by_hostname')) {
			let smartLinksBatches = groupSmartLinksByHostname(smartLinks);
			smartLinksBatches = mergeSmallThirdPartyBatches(smartLinksBatches);
			smartLinksBatches = splitLinksIntoBatches(smartLinksBatches);

			for (const smartLinksBatch of smartLinksBatches) {
				orsCalls.push(fetchORSForSSRWithTimeout(smartLinksBatch, timeout));
			}
		} else {
			const limitedSmartlinks = smartLinks.slice(
				0,
				MAX_BATCH_ORS_SMARTLINKS * MAX_CONCURRENT_ORS_CALLS,
			);
			for (let i = 0; i < limitedSmartlinks.length; i += MAX_BATCH_ORS_SMARTLINKS) {
				const chunk = limitedSmartlinks.slice(i, i + MAX_BATCH_ORS_SMARTLINKS);
				// Send smart links to linking platform
				// EX: [{resourceUrl: "https://pug.jira-dev.com/wiki/people/team/3a9665f3-c92b-423a-88bd-9410c1180058"}]
				orsCalls.push(fetchORSForSSRWithTimeout(chunk, timeout));
			}
		}

		// eslint-disable-next-line check-preloaders/no-promise-all
		const resolvedSmartLinks = (await Promise.all(orsCalls)).flat();

		// Format response to fit SmartCard Provider storeOptions initial state
		// Save correctly formatted response to window obj so it can be used on SSR and SPA (no duplicate calls)
		window['__SSR_SMARTLINKS__'] = getInitialState(resolvedSmartLinks);
		window['__SSR_SMARTLINKS_STATS__'] = getSmartLinksStats(
			orsCalls,
			smartLinks,
			resolvedSmartLinks,
		);
	});
};
